/**
 * 访问统计功能
 * 记录访问地区、当日访问量、总访问量
 * 数据保存在服务器后端
 */

// API 基础 URL
// 开发环境：使用完整 URL（因为前端和后端在不同端口）
// 生产环境：使用相对路径（通过 Nginx 代理）
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.API_BASE_URL) {
    return window.API_BASE_URL;
  }
  // 开发环境检测：如果当前是 localhost（任何端口），使用完整后端 URL
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:35001/api';
  }
  // 生产环境：使用相对路径
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// 获取今天的日期字符串 (YYYY-MM-DD)
function getTodayString() {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

// 从服务器加载统计数据
async function loadStats() {
  const url = `${API_BASE_URL}/stats`;
  console.log('=== Loading Stats ===');
  console.log('URL:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // 添加 mode 和 credentials 以确保 CORS 正常工作
      mode: 'cors',
      credentials: 'omit'
    });
    
    console.log('Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      const text = await response.text();
      console.error('Response not OK:', text);
      throw new Error(`Failed to fetch stats: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('✅ Stats loaded successfully:', data);
    console.log('Total visits:', data.totalVisits);
    console.log('Regions:', data.regions);
    return data;
  } catch (error) {
    console.error('❌ Failed to load stats:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    console.warn('提示：请确保后端服务器正在运行（npm run server）');
    console.warn('API URL:', API_BASE_URL);
    console.warn('尝试访问:', `${API_BASE_URL}/stats`);
    
    // 返回空数据
    const emptyStats = {
      totalVisits: 0,
      regions: {},
      dailyVisits: {},
      visits: []
    };
    console.log('Returning empty stats:', emptyStats);
    return emptyStats;
  }
}

// 获取访问者地理位置（使用免费IP地理位置API）
async function getVisitorLocation() {
  // 优先走后端（后端能拿到真实 IP，且避免浏览器侧被限流/被拦截）
  try {
    const response = await fetch(`${API_BASE_URL}/geo`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      mode: 'cors',
      credentials: 'omit'
    });
    if (response.ok) {
      const data = await response.json();
      if (data && data.region && data.region !== 'Unknown') {
        console.log('✅ Location from backend /geo:', data.region);
        return {
          region: data.region,
          countryCode: data.countryCode || null,
          latitude: data.latitude || null,
          longitude: data.longitude || null
        };
      }
      console.warn('⚠️ Backend /geo returned Unknown, falling back to public APIs');
    } else {
      console.warn('Backend /geo failed:', response.status, response.statusText);
    }
  } catch (e) {
    console.warn('Backend /geo request error:', e.message);
  }

  // 使用 AbortController 实现超时
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000); // 6秒超时
  
  try {
    // 使用 ipapi.co 免费API（无需API key，有速率限制）
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`ipapi.co failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 检查是否有错误
    if (data.error) {
      throw new Error(`ipapi.co error: ${data.reason || 'Unknown error'}`);
    }
    
    const region = data.country_name || data.country_code || null;
    if (!region) {
      throw new Error('No region data from ipapi.co');
    }
    
    console.log('✅ Location from ipapi.co:', region);
    return {
      region: region,
      countryCode: data.country_code || null,
      latitude: data.latitude || null,
      longitude: data.longitude || null
    };
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.warn('⏱️ ipapi.co timeout');
    } else {
      console.warn('ipapi.co failed:', error.message);
    }
    
    // 如果API失败，尝试备用API
    const backupController = new AbortController();
    const backupTimeoutId = setTimeout(() => backupController.abort(), 6000);
    
    try {
      console.log('Trying backup API: ip-api.com');
      const response = await fetch('https://ip-api.com/json/?fields=country,countryCode,lat,lon', {
        signal: backupController.signal
      });
      
      clearTimeout(backupTimeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.country && data.status === 'success') {
          console.log('✅ Location from ip-api.com:', data.country);
          return {
            region: data.country,
            countryCode: data.countryCode || null,
            latitude: data.lat || null,
            longitude: data.lon || null
          };
        }
      }
    } catch (e) {
      clearTimeout(backupTimeoutId);
      if (e.name === 'AbortError') {
        console.warn('⏱️ ip-api.com timeout');
      } else {
        console.error('Backup API also failed:', e.message);
      }
    }
    
    // 如果所有API都失败，返回 Unknown
    console.warn('⚠️ All location APIs failed, using Unknown');
    return {
      region: 'Unknown',
      countryCode: null,
      latitude: null,
      longitude: null
    };
  }
}

// 记录访问（防止重复调用）
let isRecording = false;
let lastRecordTime = 0;
const MIN_RECORD_INTERVAL = 2000; // 最小记录间隔：2秒

// 记录访问
async function recordVisit() {
  // 防止重复调用
  if (isRecording) {
    console.log('⏸️ Already recording, skipping...');
    return;
  }
  
  const now = Date.now();
  if (now - lastRecordTime < MIN_RECORD_INTERVAL) {
    console.log('⏸️ Too soon since last record, skipping...');
    return;
  }
  
  // 检查今天是否已经记录过（使用 sessionStorage 防止刷新重复计数）
  const today = getTodayString();
  const sessionKey = `visited_${today}`;
  const visitTimestamp = sessionStorage.getItem(sessionKey);
  
  if (visitTimestamp) {
    const visitTime = parseInt(visitTimestamp, 10);
    const timeSinceVisit = now - visitTime;
    // 如果5分钟内已经记录过，不重复记录
    if (timeSinceVisit < 5 * 60 * 1000) {
      console.log('⏸️ Already visited today (within 5 minutes), only updating display');
      const stats = await loadStats();
      updateDisplay(stats);
      return;
    }
  }
  
  isRecording = true;
  lastRecordTime = now;
  
  try {
    console.log('📍 Getting visitor location...');
    // 获取访问者位置（添加超时处理）
    const locationPromise = getVisitorLocation();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Location API timeout')), 8000)
    );
    
    const location = await Promise.race([locationPromise, timeoutPromise]);
    
    console.log('📍 Location obtained:', location.region);
    
    // 发送到服务器
    const response = await fetch(`${API_BASE_URL}/visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(location)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to record visit: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // 标记今天已访问（记录时间戳）
    sessionStorage.setItem(sessionKey, now.toString());
    
    console.log('✅ Visit recorded successfully');
    
    // 更新显示
    updateDisplay(result.stats);
  } catch (error) {
    console.error('❌ Failed to record visit:', error);
    console.warn('提示：请确保后端服务器正在运行（npm run server）');
    // 即使失败也尝试加载现有数据
    const stats = await loadStats();
    updateDisplay(stats);
  } finally {
    isRecording = false;
  }
}

// 更新显示
function updateDisplay(stats) {
  console.log('=== Updating Display ===');
  console.log('Stats object:', stats);
  console.log('Stats type:', typeof stats);
  console.log('Stats keys:', Object.keys(stats || {}));
  
  if (!stats) {
    console.error('Stats is null or undefined!');
    return;
  }
  
  const today = getTodayString();
  console.log(`Today string: "${today}"`);
  console.log(`dailyVisits object:`, stats.dailyVisits);
  console.log(`dailyVisits keys:`, Object.keys(stats.dailyVisits || {}));
  
  // 检查日期匹配
  const todayVisits = stats.dailyVisits?.[today] || 0;
  const totalVisits = stats.totalVisits || 0;
  
  console.log(`Today: ${today}`);
  console.log(`Today visits (from dailyVisits[${today}]): ${todayVisits}`);
  console.log(`Total visits: ${totalVisits}`);
  console.log(`Regions:`, stats.regions);
  console.log(`Regions keys:`, Object.keys(stats.regions || {}));
  
  // 更新数字显示
  const todayElement = document.getElementById('stats-today');
  const totalElement = document.getElementById('stats-total');
  
  console.log('Today element:', todayElement);
  console.log('Total element:', totalElement);
  
  if (todayElement) {
    todayElement.textContent = todayVisits.toLocaleString();
    console.log('✅ Updated today visits to:', todayVisits);
  } else {
    console.error('❌ stats-today element not found!');
  }
  
  if (totalElement) {
    totalElement.textContent = totalVisits.toLocaleString();
    console.log('✅ Updated total visits to:', totalVisits);
  } else {
    console.error('❌ stats-total element not found!');
  }
  
  // 更新地区列表
  console.log('Updating region list with:', stats.regions);
  updateRegionList(stats.regions || {});
  
  // 更新地图
  console.log('Updating map with visits:', stats.visits);
  updateMap(stats.visits || []);
  
  console.log('=== Display Update Complete ===');
}

// 更新地区列表显示
function updateRegionList(regions) {
  const regionListElement = document.getElementById('stats-regions');
  if (!regionListElement) {
    console.warn('stats-regions element not found');
    return;
  }
  
  console.log('Updating region list with:', regions);
  
  // 检查是否有数据
  if (!regions || Object.keys(regions).length === 0) {
    // 使用国际化文本，但这里暂时用硬编码，因为i18n可能还没准备好
    const noDataText = document.documentElement.lang === 'zh' 
      ? '暂无数据（请确保后端服务器正在运行）'
      : 'No data (Please ensure backend server is running)';
    regionListElement.innerHTML = `<li class="stats-region-item">${noDataText}</li>`;
    return;
  }
  
  // 按访问量排序
  const sortedRegions = Object.entries(regions)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10); // 只显示前10个地区
  
  if (sortedRegions.length === 0) {
    const noDataText = document.documentElement.lang === 'zh' 
      ? '暂无数据'
      : 'No data';
    regionListElement.innerHTML = `<li class="stats-region-item">${noDataText}</li>`;
    return;
  }
  
  const total = Object.values(regions).reduce((a, b) => a + b, 0);
  console.log(`Total visits: ${total}, Regions: ${sortedRegions.length}`);
  
  regionListElement.innerHTML = sortedRegions
    .map(([region, count]) => {
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      return `
        <li class="stats-region-item">
          <span class="stats-region-name">${region}</span>
          <span class="stats-region-count">${count}</span>
          <span class="stats-region-bar">
            <span class="stats-region-bar-fill" style="width: ${percentage}%"></span>
          </span>
        </li>
      `;
    })
    .join('');
}

// 更新地图显示
function updateMap(visits) {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer) return;
  
  // 如果地图已经初始化，更新标记
  if (window.visitMap) {
    // 清除现有标记
    window.visitMap.markers.forEach(marker => marker.remove());
    window.visitMap.markers = [];
    
    // 添加新标记
    addMarkersToMap(window.visitMap.map, visits);
    return;
  }
  
  // 初始化地图
  initMap(visits);
}

// 初始化地图
function initMap(visits) {
  const mapContainer = document.getElementById('visit-map');
  if (!mapContainer) return;
  
  // 检查 Leaflet 是否已加载，如果没有则等待
  if (typeof L === 'undefined') {
    console.warn('Leaflet is not loaded yet, retrying...');
    setTimeout(() => initMap(visits), 500);
    return;
  }
  
  // 创建地图（默认中心点：世界中心）
  const map = L.map('visit-map').setView([20, 0], 2);
  
  // 添加 OpenStreetMap 瓦片图层
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);
  
  // 存储地图和标记数组
  window.visitMap = {
    map: map,
    markers: []
  };
  
  // 添加标记
  addMarkersToMap(map, visits);
}

// 添加标记到地图
function addMarkersToMap(map, visits) {
  if (!window.visitMap) return;
  
  // 按国家/地区分组统计
  const locationCounts = {};
  visits.forEach(visit => {
    const key = visit.latitude && visit.longitude 
      ? `${visit.latitude},${visit.longitude}`
      : visit.region;
    
    if (!locationCounts[key]) {
      locationCounts[key] = {
        count: 0,
        region: visit.region,
        latitude: visit.latitude,
        longitude: visit.longitude
      };
    }
    locationCounts[key].count++;
  });
  
  // 为每个位置添加标记
  Object.values(locationCounts).forEach(location => {
    let lat = location.latitude;
    let lng = location.longitude;
    
    // 如果没有坐标，尝试从国家名称获取（使用简单的映射）
    if (!lat || !lng) {
      const coords = getCountryCoordinates(location.region);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      } else {
        return; // 跳过没有坐标的位置
      }
    }
    
    // 创建标记
    const marker = L.circleMarker([lat, lng], {
      radius: Math.min(Math.max(location.count * 2, 5), 30), // 根据访问量调整大小
      fillColor: '#02aab0',
      color: '#fff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.7
    }).addTo(map);
    
    // 添加弹出窗口
    marker.bindPopup(`
      <strong>${location.region}</strong><br>
      访问量: ${location.count}
    `);
    
    window.visitMap.markers.push(marker);
  });
}

// 简单的国家名称到坐标映射（常用国家）
function getCountryCoordinates(countryName) {
  const countryMap = {
    'China': { lat: 35.8617, lng: 104.1954 },
    'United States': { lat: 37.0902, lng: -95.7129 },
    'United Kingdom': { lat: 55.3781, lng: -3.4360 },
    'Canada': { lat: 56.1304, lng: -106.3468 },
    'Australia': { lat: -25.2744, lng: 133.7751 },
    'Germany': { lat: 51.1657, lng: 10.4515 },
    'France': { lat: 46.2276, lng: 2.2137 },
    'Japan': { lat: 36.2048, lng: 138.2529 },
    'South Korea': { lat: 35.9078, lng: 127.7669 },
    'India': { lat: 20.5937, lng: 78.9629 },
    'Brazil': { lat: -14.2350, lng: -51.9253 },
    'Russia': { lat: 61.5240, lng: 105.3188 },
    'Singapore': { lat: 1.3521, lng: 103.8198 },
    'Hong Kong': { lat: 22.3193, lng: 114.1694 },
    'Taiwan': { lat: 23.6978, lng: 120.9605 },
    'Unknown': null
  };
  
  return countryMap[countryName] || null;
}

// 初始化统计功能
export default function initVisitStats() {
  console.log('=== Visit Stats Initialization ===');
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('Current URL:', window.location.href);
  console.log('Hostname:', window.location.hostname);
  console.log('Port:', window.location.port);
  
  // 等待 DOM 完全加载
  const initStats = () => {
    console.log('DOM ready, initializing stats...');
    
    // 先加载一次数据并显示（不记录访问，只是显示已有数据）
    loadStats().then(stats => {
      console.log('Initial stats loaded:', stats);
      updateDisplay(stats);
    }).catch(error => {
      console.error('Failed to load initial stats:', error);
    });
    
    // 然后记录访问
    console.log('Recording visit...');
    recordVisit();
    
    // 定期更新显示
    setInterval(async () => {
      console.log('Periodic update...');
      const stats = await loadStats();
      updateDisplay(stats);
    }, 10000); // 每10秒更新一次
  };
  
  if (document.readyState === 'loading') {
    console.log('Waiting for DOM to load...');
    document.addEventListener('DOMContentLoaded', initStats);
  } else {
    console.log('DOM already loaded');
    // 延迟一点确保所有元素都已渲染
    setTimeout(initStats, 100);
  }
}
